/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import metatags from '../../../../data/metaConfig.json';
import Navbar from '../../../components/NavBar/Navbar';
import Footer from '../../../components/Footer/Footer';
// $FlowFixMe
import TrailheadFooter from 'cchome-shared/components/Footer/Footer'; // cchome-shared has been ignored in .flowconfig
import { getEnvironment } from '../../../../utils/Environment';
import { replaceURLWithLocale } from '../../../../utils/Locale';
import AnalyticsUtils from '../../../../utils/AnalyticsUtil';
import TrailheadFooterAnalytics from '../../../../utils/TrailheadFooterAnalytics';
import AppsUtils from '../../Apps/AppsUtils';
import { CCHomeAppsCCDUtil } from 'cchome-apps';

/* eslint-enable no-unused-vars */

import './App.scss';
import { renderRoutes, matchRoutes } from 'react-router-config';
import { injectIntl } from 'react-intl';

interface Props {
	app: any;
	profile: any;
	route: {
		routes: any[];
	};
	location: {
		pathname: string;
	};
	history: any;
	intl: any;
}

class App extends Component<Props> {
	analytics: any;

	constructor(props) {
		super(props);
		if (window.cchome.util.optOutAnalyticsInited) {
			this.onSendAnalyticsInited();
		}
		window.addEventListener('analyticsOptinInited', this.onSendAnalyticsInited.bind(this));
		if (this.isCCDownloadPage()) {
			this.preloadImages();
		}
		this.analytics = new TrailheadFooterAnalytics();
	}

	/**
	 * Called when we get the return value from the PIIP service.
	 */
	onSendAnalyticsInited() {
		// this.props.setAnalyticsOptinInited(true);
	}
	addMetaTags() {
		const path = window.location.pathname;
		let tags: any = metatags.default;
		if (path.indexOf('/quick-actions') !== -1) {
			tags = metatags.quickActions;
		} else if (path.indexOf('apps/download/creative-cloud') !== -1) {
			tags = metatags.ccd;
		}
		tags.map((tag) => {
			const meta = document.createElement('meta');
			const keys = Object.keys(tag);
			const values = Object.values(tag);
			for (let i = 0; i < keys.length; i++) {
				let value = tag[keys[i]];
				if (value.indexOf('app.meta') !== -1) {
					value = this.props.intl.formatMessage({ id: values[i] });
				}
				meta.setAttribute(keys[i], value);
				document.getElementsByTagName('head')[0].appendChild(meta);
			}
			return meta;
		});
	}

	componentDidMount() {
		this.addMetaTags();
		this.updateDocumentTitle();
		const reactSpectrumProvider = document.querySelector('.react-spectrum-provider') as HTMLElement;
		if (this.isCCDownloadPage() && reactSpectrumProvider) {
			reactSpectrumProvider.style.height = '100%';
		}
	}

	// Pre-load images to improve turn-around time.
	preloadImages() {
		const appPickerImageURLs = [
			'/assets/ccd/images/trailhead-background.jpg',
			'/assets/ccd/images/Apps-still.png',
			'/assets/ccd/images/cc-mnemonic.png',
			'/assets/ccd/images/Marketplace-still.jpg',
			'/assets/ccd/images/Your-work-still.jpg',
			'/assets/ccd/images/Discover-still.jpg',
		];
		appPickerImageURLs.map((url) => {
			const img = new Image();
			img.src = url;
			return img;
		});
	}

	/* istanbul ignore next */
	componentDidUpdate(prevProps) {}

	componentWillUnmount() {
		window.removeEventListener('analyticsOptinInited', this.onSendAnalyticsInited.bind(this));
		const reactSpectrumProvider = document.querySelector('.react-spectrum-provider') as HTMLElement;
		if (this.isCCDownloadPage() && reactSpectrumProvider) {
			reactSpectrumProvider.style.height = 'initial';
		}
	}

	updateDocumentTitle() {
		let titleId = '';

		const matches = matchRoutes(this.props.route.routes, this.props.location.pathname);

		/* istanbul ignore else */
		if (matches && matches.length > 0) {
			titleId = matches[0].route.titleId;
		}

		if (!titleId || titleId === '') {
			titleId = 'app.document.title';
		}

		window.cchome.util.setDocumentTitle(this.props.intl.formatMessage({ id: titleId }));
	}

	getMainContainerStyle(pathObject) {
		let className = 'main-container fullscreen';
		if (pathObject.pageType === 'campaign') {
			className = `${className} campaign`;
		}
		return className;
	}

	isCCD() {
		const pathObject = AppsUtils.getPathData();
		return pathObject.mainTab && pathObject.mainTab.toLowerCase() === 'download';
	}

	isCCDownloadPage() {
		if (!window.cchome.util.hasFeature('temp_cc_download_page')) {
			return false;
		}
		const pathObject = AppsUtils.getPathData();
		return this.isCCD() && pathObject.productId === 'creative-cloud' && pathObject.pageType === undefined;
	}

	render() {
		const env = getEnvironment();
		const lang = this.props.app.language || 'en';
		const filteredRoutes = this.props.route.routes;
		const pathObject = AppsUtils.getPathData();
		const isCCDownloadPage = this.isCCDownloadPage();
		const footerContext = {
			analyticsCallbackHandler: (data) => {
				this.analytics.sendFooterEvent(data.format1);
				return Promise.resolve(); // Caller expects a promise and action is initiated after returned promise is resolved
			},
			setLocale: this.analytics.setLocale,
			downloadCC: () => {
				CCHomeAppsCCDUtil.downloadCC();
			},
		};

		/* istanbul ignore else */
		if (document.documentElement) {
			document.documentElement.lang = lang;
		}

		/* set browser type */
		if (document.documentElement) {
			document.documentElement.setAttribute('data-browser', window.cchome.util.getBrowserType());
		}
		/* eslint-disable */
		return (
			<div className={`app-container ${isCCDownloadPage ? 'cc-download-scrollable-page' : ''}`}>
				<div className={this.getMainContainerStyle(pathObject)}>
					<div className="center-container">
						<Navbar
							// @ts-ignore
							locale={lang}
							isLoggedOutExp
							isLoggedOutCampaignExp={pathObject.pageType === 'campaign'}
							environment={env}
						/>
						{renderRoutes(filteredRoutes, {
							locale: lang,
							env,
						})}
					</div>
				</div>
				{isCCDownloadPage ? (
					<TrailheadFooter
						source="apps"
						intl={this.props.intl}
						environment={env}
						handleLanguageChange={replaceURLWithLocale}
						appendUrlWithAnalytics={(localizedUrl) => AnalyticsUtils.appendUrlWithAnalytics(localizedUrl)}
						footerContext={footerContext}
					/>
				) : (
					<Footer
						locale={lang}
						hideLanguageSwitcher={pathObject.page && pathObject.page.toLowerCase() === 'quick-actions'}
						isCCD={pathObject.mainTab && pathObject.mainTab.toLowerCase() === 'download'}
					/>
				)}
			</div>
		);

		/* eslint-enable */
	}
}

export default {
	component: injectIntl(App),
};
